<template>
  <!-- フレーム領域 -->
  <div class="ja-content-frame">
    <!-- フレームヘッダー領域 -->
    <div class="frame-header-area">
      <span class="header-title">フレーム</span>
      <!-- もっと見るボタン -->
      <button
        v-if="isEditing"
        class="more-frame-button d-flex justify-center items-center"
        @click="showMoreFrame"
      >
        <div class="frame-header-text-area">
          <span class="frame-header-text">もっとみる</span>
          <v-ons-icon class="icon-right-arrow" icon="md-chevron-right" size="18px" />
        </div>
      </button>
    </div>
    <!-- 直近で利用したコンテンツの表示領域 -->
    <div class="horizon-scroll-area" v-if="isEditing">
      <!-- フレームなしの表示 -->
      <ja-content-frame-cell
        :key="NONE_FRAME_BOTTOM.frameId"
        :contentFrame="NONE_FRAME_BOTTOM"
        :isSelectedContentFrame="NONE_FRAME_BOTTOM.frameId === selectedContentFrame.frameId"
        :selectContentFrame="selectContentFrame"
        class="first-frame"
      />
      <ja-content-frame-cell
        :key="NONE_FRAME_TOP.frameId"
        :contentFrame="NONE_FRAME_TOP"
        :isSelectedContentFrame="NONE_FRAME_TOP.frameId === selectedContentFrame.frameId"
        :selectContentFrame="selectContentFrame"
        class="second-frame"
      />
      <!-- JaContentFrameSelectの表示(最新５件の中から選択) -->
      <ja-content-frame-cell
        v-for="contentFrame in contentFrames"
        :key="contentFrame.frameId"
        :contentFrame="contentFrame"
        :isSelectedContentFrame="contentFrame.frameId === selectedContentFrame.frameId"
        :selectContentFrame="selectContentFrame"
      />
      <!-- もっと見るの表示 -->
      <ja-content-frame-cell
        :contentFrame="NONE_FRAME_BOTTOM"
        :isSelectedContentFrame="false"
        :selectContentFrame="showMoreFrame"
        class="more-frame-icon"
      />
    </div>
    <!-- コンテンツ詳細(未編集)画面のフレーム表示 -->
    <div v-else>
      <ja-content-frame-cell
        :contentFrame="initialSelectedContentFrame"
        :isSelectedContentFrame="false"
        :class="[
          { 'first-frame': initialSelectedContentFrame.frameId === NONE_FRAME_BOTTOM.frameId },
          { 'second-frame': initialSelectedContentFrame.frameId === NONE_FRAME_TOP.frameId },
        ]"
        disabled
      />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import JaContentFrameCell from '@/components/JaContent/JaContentFrameCell.vue';
import { dataFetchContentFramesHistory } from '@/composable/data_fetch_common';
import { NONE_FRAME_TOP, NONE_FRAME_BOTTOM } from '@/composable/constant_common';

const store = useStore();

const props = defineProps({
  initialSelectedContentFrame: { type: Object, default: () => ({}) },
  showMoreFrame: Function,
  isEditing: Boolean,
});

const contentFrames = ref([]);
const selectedContentFrame = ref({});
const isLoadingDone = ref(false);
const emit = defineEmits(['selectFrame']);

const getContentFrames = async () => {
  isLoadingDone.value = false;
  contentFrames.value = await dataFetchContentFramesHistory();

  // 5件の中にフレームなしが含まれている場合は削除する
  contentFrames.value = contentFrames.value.filter(
    (frame) => frame.frameId !== NONE_FRAME_TOP.frameId
  );

  // 4件に変更するため最後の要素を取り除く
  if (contentFrames.value.length === 5) {
    contentFrames.value.pop();
  }
  isLoadingDone.value = true;
};

const selectContentFrame = (selectContentFrameParam) => {
  selectedContentFrame.value = selectContentFrameParam;
  store.commit('content/setContentFrame', selectedContentFrame.value);
  emit('selectFrame', selectContentFrameParam);
};

const getRecentContentFrames = () => {
  if (
    selectedContentFrame.value.frameId !== NONE_FRAME_BOTTOM.frameId &&
    selectedContentFrame.value.frameId !== NONE_FRAME_TOP.frameId
  ) {
    const contentIndex = contentFrames.value.findIndex(
      (contentFrame) => contentFrame.frameId === selectedContentFrame.value.frameId
    );

    if (contentIndex !== -1) {
      contentFrames.value.splice(contentIndex, 1);
    }
    contentFrames.value.unshift(selectedContentFrame.value);
    contentFrames.value = contentFrames.value.slice(0, 5);
  }
};

// Watchers
watch(
  () => props.initialSelectedContentFrame,
  (newVal) => {
    selectedContentFrame.value = newVal;
  },
  { deep: true }
);

watch(
  () => props.isEditing,
  () => {
    getRecentContentFrames();
  }
);

onMounted(async () => {
  await getContentFrames();
  selectedContentFrame.value = props.initialSelectedContentFrame;
  getRecentContentFrames();
});
</script>

<style scoped>
.frame-header-area {
  position: relative;
  height: 3.5rem;
  font-size: 1.3rem;
}
.more-frame-button {
  font-size: 1.1rem;
  background: #fff;
  border: solid 1px #cccccc;
  color: #222222;
  border-radius: 0.5rem;
  position: absolute;
  right: 0.5rem;
  top: 0rem;
  height: 2.3rem;
  width: 8rem;
}
.frame-header-text-area {
  position: absolute;
  top: 0.2rem;
  height: 2rem;
  width: 8rem;
}
.frame-header-text {
  position: absolute;
  left: 0.5rem;
  font-size: 1.1rem;
  text-align: center;
}
.icon-right-arrow {
  position: absolute;
  right: 1rem;
  bottom: 0.2rem;
  color: #cccccc;
  margin-left: 4px;
  font-weight: bold;
}
.horizon-scroll-area {
  display: flex;
  overflow-x: scroll;
  flex-wrap: nowrap;
  gap: 1rem;
  padding-bottom: 1rem;
}
.first-frame:before {
  content: 'なし\Aテロップ下';
  white-space: pre;
  color: #aaaaaa;
  font-size: 14px;
}
.second-frame:before {
  content: 'テロップ上\Aなし';
  white-space: pre;
  color: #aaaaaa;
  font-size: 14px;
}
.more-frame-icon:before {
  content: 'もっとみる';
  color: #222222;
  font-size: 14px;
}
.more-frame-icon:after {
  content: '>';
  color: #aaaaaa;
  font-size: 14px;
  padding-left: 10px;
  font-weight: bold;
}
.first-frame,
.second-frame,
.more-frame-icon {
  background-color: #f2f2f2;
  border: solid 1px #dddddd;
}
</style>
